import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { hooks } from '../hooks';
import { text } from '../text';
import { theme } from '../constants';
import { components } from '../components';
import { custom } from '../custom';
import axiosInstance from '../services/axiosInstance';
import { MdSearch, MdCheckBox, MdCheckBoxOutlineBlank } from 'react-icons/md';

const SearchIcon = MdSearch as any;
const CheckBoxIcon = MdCheckBox as any;
const CheckBoxOutlineBlankIcon = MdCheckBoxOutlineBlank as any;

const CAT_ORDER = ['tools', 'installation items', 'consumables'];
const CAT_LABELS: Record<string, string> = {
  tools: 'Tools',
  'installation items': 'Installation Items',
  consumables: 'Consumables',
};
const CAT_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  tools:              { bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
  'installation items': { bg: '#F0FDF4', color: '#15803D', border: '#86EFAC' },
  consumables:        { bg: '#FFF7ED', color: '#C2410C', border: '#FED7AA' },
};

export const PreCheckReportForm: React.FC = () => {
  const { state } = useLocation();
  const navigate = hooks.useAppNavigate();
  const { t } = useLanguage();
  const tr = (t as any).preCheckReport || {};

  const mode: 'create' | 'edit' = state?.mode || 'create';
  const reportId: number | undefined = state?.reportId;

  // Form fields
  const [reportType, setReportType] = useState<'persiapan' | 'pemeriksaan'>('persiapan');
  const [reportDate, setReportDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [note, setNote] = useState('');
  const [status, setStatus] = useState<'draft' | 'ready'>('draft');

  // Vessel
  const [vessels, setVessels] = useState<any[]>([]);
  const [vesselId, setVesselId] = useState<number | null>(null);
  const [vesselSearch, setVesselSearch] = useState('');
  const [showVesselDropdown, setShowVesselDropdown] = useState(false);

  // Checklist
  const [availableItems, setAvailableItems] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [itemQty, setItemQty] = useState<Record<number, number>>({});
  const [itemSerial, setItemSerial] = useState<Record<number, string>>({});
  const [itemChecked, setItemChecked] = useState<Record<number, boolean>>({});
  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItemTitle, setNewItemTitle] = useState('');
  const [newItemCategory, setNewItemCategory] = useState('');

  // Load vessels + checklist items
  useEffect(() => {
    axiosInstance.get('/vessels')
      .then(res => setVessels(res.data))
      .catch(() => {});
    axiosInstance.get('/tasks/checker-checklists')
      .then(res => setAvailableItems(res.data))
      .catch(err => console.error('Failed to load checklists:', err));
  }, []);

  // Load existing report if editing
  useEffect(() => {
    if (mode === 'edit' && reportId) {
      setLoading(true);
      axiosInstance.get(`/checker-reports/${reportId}`)
        .then(res => {
          const r = res.data;
          setReportType(r.type);
          setReportDate(r.report_date?.split('T')[0] || r.report_date);
          setNote(r.note || '');
          setStatus(r.status);
          if (r.vessel_id) setVesselId(r.vessel_id);
          if (r.items?.length > 0) {
            const ids = r.items.map((i: any) => i.checker_checklist_id);
            const qty: Record<number, number> = {};
            const serial: Record<number, string> = {};
            const checked: Record<number, boolean> = {};
            r.items.forEach((i: any) => {
              qty[i.checker_checklist_id] = i.qty ?? 1;
              serial[i.checker_checklist_id] = i.serial_number || '';
              checked[i.checker_checklist_id] = i.is_checked ?? false;
            });
            setSelectedIds(ids);
            setItemQty(qty);
            setItemSerial(serial);
            setItemChecked(checked);
          }
        })
        .catch(err => console.error('Failed to load report:', err))
        .finally(() => setLoading(false));
    }
  }, [mode, reportId]);

  const toggleItem = (id: number) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const buildItems = () =>
    selectedIds.map(id => {
      const item = availableItems.find(i => i.id === id);
      // Serial number cuma relevan utk "installation items" (lihat input-nya di renderSelectedItems)
      // — jangan ikut tersimpan utk kategori lain, mis. sisa dari draft lama sebelum field ini
      // dibatasi per-kategori, atau kalau kategori item diubah setelah serial sempat diisi.
      const isInstallation = item?.category === 'installation items';
      return {
        checker_checklist_id: id,
        is_checked: itemChecked[id] ?? false,
        qty: itemQty[id] ?? 1,
        serial_number: isInstallation ? (itemSerial[id] || null) : null,
      };
    });

  const handleSave = async (saveStatus: 'draft' | 'ready') => {
    setSaving(true);
    try {
      const payload = {
        type: reportType,
        report_date: reportDate,
        note: note || null,
        status: saveStatus,
        vessel_id: vesselId || null,
        items: buildItems(),
      };

      if (mode === 'create') {
        await axiosInstance.post('/checker-reports', payload);
      } else {
        await axiosInstance.put(`/checker-reports/${reportId}`, payload);
      }
      navigate(-1);
    } catch (e: any) {
      alert(e?.response?.data?.message || (tr.saveFailed || 'Gagal menyimpan laporan'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItemTitle.trim()) return;
    try {
      const res = await axiosInstance.post('/tasks/checker-checklists', {
        title: newItemTitle,
        category: newItemCategory || null,
      });
      setAvailableItems(prev => [...prev, res.data].sort((a, b) => a.title.localeCompare(b.title)));
      setNewItemTitle('');
      setNewItemCategory('');
      setShowAddModal(false);
    } catch {
      alert(tr.addItemFailed || 'Gagal menambah item');
    }
  };

  const filteredItems = availableItems.filter(i =>
    i.title.toLowerCase().includes(search.toLowerCase())
  );

  const renderAddModal = () => {
    if (!showAddModal) return null;
    return (
      <div style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 4000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20,
      }}>
        <div style={{
          backgroundColor: 'white', borderRadius: 16, padding: 24,
          width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column',
        }}>
          <text.H4 style={{ marginBottom: 16 }}>{tr.addItemTitle || 'Tambah Item Checklist'}</text.H4>
          <input
            type="text"
            placeholder={tr.addItemPlaceholder || 'Contoh: Helm Safety, Kunci Inggris...'}
            value={newItemTitle}
            onChange={e => setNewItemTitle(e.target.value)}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 8,
              border: '1px solid #E5E7EB', fontSize: 16, outline: 'none',
              marginBottom: 12, boxSizing: 'border-box', fontFamily: 'inherit',
            }}
          />
          <text.T14 style={{ color: '#6B7280', marginBottom: 6 }}>
            {tr.addItemCategoryLabel || 'Kategori'}
          </text.T14>
          <select
            value={newItemCategory}
            onChange={e => setNewItemCategory(e.target.value)}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 8,
              border: '1px solid #E5E7EB', fontSize: 16, outline: 'none',
              marginBottom: 20, boxSizing: 'border-box', fontFamily: 'inherit',
              backgroundColor: '#fff', color: theme.colors.mainDark,
            }}
          >
            <option value="">{tr.addItemCategoryPlaceholder || 'Tanpa kategori'}</option>
            {CAT_ORDER.map(cat => (
              <option key={cat} value={cat}>{CAT_LABELS[cat] || cat}</option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: 10 }}>
            <components.Button
              title={t.common?.cancel || 'Batal'}
              style={{ backgroundColor: '#F3F4F6', color: '#4B5563', padding: '10px' }}
              containerStyle={{ flex: 1 }}
              onClick={() => setShowAddModal(false)}
            />
            <components.Button
              title={t.common?.save || 'Simpan'}
              style={{ padding: '10px' }}
              containerStyle={{ flex: 1 }}
              onClick={handleAddItem}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderChecklist = () => {
    const groups: { cat: string | null; items: any[] }[] = [];
    CAT_ORDER.forEach(cat => {
      const items = filteredItems.filter(i => i.category === cat);
      if (items.length > 0) groups.push({ cat, items });
    });
    const uncategorized = filteredItems.filter(i => !i.category || !CAT_ORDER.includes(i.category));
    if (uncategorized.length > 0) groups.push({ cat: null, items: uncategorized });

    return (
      <div className="scrollbar-visible" style={{
        border: '1px solid #E5E7EB', borderRadius: 10, backgroundColor: '#FFFFFF',
        maxHeight: 300, overflowY: 'auto', padding: 8,
      }}>
        {groups.length === 0 && (
          <div style={{ padding: '24px 16px', textAlign: 'center' }}>
            <text.T14 style={{ color: '#9CA3AF', fontStyle: 'italic' }}>
              {tr.noItems || 'Tidak ada item ditemukan.'}
            </text.T14>
          </div>
        )}
        {groups.map(({ cat, items }, gIdx) => {
          const catStyle = cat ? CAT_COLORS[cat] : { bg: '#F9FAFB', color: '#374151', border: '#E5E7EB' };
          return (
            <div key={cat ?? 'none'}>
              {cat && (
                <div style={{
                  padding: '5px 10px',
                  backgroundColor: catStyle.bg,
                  borderBottom: `1px solid ${catStyle.border}`,
                  ...(gIdx > 0 ? { borderTop: '1px solid #E5E7EB' } : {}),
                }}>
                  <text.T14 style={{
                    color: catStyle.color, fontWeight: '700',
                    fontSize: 11, textTransform: 'uppercase' as any, letterSpacing: 0.5,
                  }}>
                    {CAT_LABELS[cat] || cat}
                  </text.T14>
                </div>
              )}
              {items.map((item, iIdx) => {
                const isSelected = selectedIds.includes(item.id);
                return (
                  <div
                    key={item.id}
                    onClick={() => toggleItem(item.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                      backgroundColor: isSelected ? '#F0FDF4' : '#FFF',
                      cursor: 'pointer',
                      borderBottom: iIdx < items.length - 1 ? '1px solid #F3F4F6' : 'none',
                    }}
                  >
                    {isSelected
                      ? <CheckBoxIcon size={22} color={theme.colors.mainColor} />
                      : <CheckBoxOutlineBlankIcon size={22} color="#9CA3AF" />
                    }
                    <text.T14 style={{ color: isSelected ? theme.colors.mainDark : '#4B5563', flex: 1 }}>
                      {item.title}
                    </text.T14>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  const renderSelectedItems = () => {
    const selectedItems = availableItems.filter(i => selectedIds.includes(i.id));
    if (selectedItems.length === 0) return null;

    const groups: { cat: string | null; items: any[] }[] = [];
    CAT_ORDER.forEach(cat => {
      const items = selectedItems.filter(i => i.category === cat);
      if (items.length > 0) groups.push({ cat, items });
    });
    const uncategorized = selectedItems.filter(i => !i.category || !CAT_ORDER.includes(i.category));
    if (uncategorized.length > 0) groups.push({ cat: null, items: uncategorized });

    return (
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <text.H5 style={{ margin: 0 }}>{tr.selectedItems || 'Item yang Dipilih'}</text.H5>
          <div style={{
            backgroundColor: theme.colors.mainColor, color: '#fff',
            borderRadius: 99, minWidth: 22, height: 22,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: '700', padding: '0 7px',
          }}>
            {selectedItems.length}
          </div>
        </div>
        <div style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {groups.map(({ cat, items }, gIdx) => (
            <div key={cat ?? 'none'}>
              {cat && (
                <div style={{ marginBottom: 4, paddingLeft: 2 }}>
                  <text.T14 style={{
                    fontSize: 11, fontWeight: '700', textTransform: 'uppercase' as any,
                    letterSpacing: 0.5, color: CAT_COLORS[cat]?.color || '#374151',
                  }}>
                    {CAT_LABELS[cat] || cat}
                  </text.T14>
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {items.map(item => {
                  const qty = itemQty[item.id] ?? 1;
                  return (
                    <div key={item.id} style={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #BBF7D0', padding: '8px 10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CheckBoxIcon size={18} color={theme.colors.mainColor} style={{ flexShrink: 0 }} />
                        <text.T14 style={{ color: '#166534', flex: 1, fontWeight: '500' }}>{item.title}</text.T14>
                        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                          <button
                            onClick={() => setItemQty(prev => ({ ...prev, [item.id]: Math.max(1, (prev[item.id] ?? 1) - 1) }))}
                            style={{ width: 28, height: 28, borderRadius: '6px 0 0 6px', border: '1px solid #D1FAE5', borderRight: 'none', background: '#E7F9EF', color: theme.colors.mainDark, fontSize: 18, fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                          >−</button>
                          <input
                            type="number" min={1} value={qty}
                            onChange={e => { const v = parseInt(e.target.value, 10); setItemQty(prev => ({ ...prev, [item.id]: isNaN(v) || v < 1 ? 1 : v })); }}
                            style={{ width: 40, height: 28, textAlign: 'center', border: '1px solid #D1FAE5', borderLeft: 'none', borderRight: 'none', fontSize: 13, fontWeight: '600', color: '#166534', outline: 'none', fontFamily: 'inherit', MozAppearance: 'textfield' as any }}
                          />
                          <button
                            onClick={() => setItemQty(prev => ({ ...prev, [item.id]: (prev[item.id] ?? 1) + 1 }))}
                            style={{ width: 28, height: 28, borderRadius: '0 6px 6px 0', border: '1px solid #D1FAE5', borderLeft: 'none', background: '#E7F9EF', color: theme.colors.mainDark, fontSize: 18, fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                          >+</button>
                        </div>
                      </div>
                      {item.category === 'installation items' && (
                        <div style={{ marginTop: 6, paddingLeft: 26 }}>
                          <input
                            type="text" placeholder="Serial number (opsional)"
                            value={itemSerial[item.id] || ''}
                            onChange={e => setItemSerial(prev => ({ ...prev, [item.id]: e.target.value }))}
                            style={{ width: '100%', padding: '5px 10px', borderRadius: 6, border: '1px solid #D1FAE5', fontSize: 13, color: '#166534', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as any, backgroundColor: '#F0FDF4' }}
                          />
                        </div>
                      )}
                      {reportType === 'pemeriksaan' && (
                        <div
                          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 6, paddingLeft: 26 }}
                          onClick={() => setItemChecked(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
                        >
                          {itemChecked[item.id]
                            ? <CheckBoxIcon size={18} color="#16A34A" />
                            : <CheckBoxOutlineBlankIcon size={18} color="#9CA3AF" />
                          }
                          <text.T14 style={{ color: '#374151', fontSize: 13 }}>
                            {tr.itemChecked || 'Item sudah dicek/tersedia'}
                          </text.T14>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {gIdx < groups.length - 1 && (
                <div style={{ height: 1, backgroundColor: '#BBF7D0', marginTop: 8 }} />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className='screen' style={{ backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
        <components.Header title={tr.titleEdit || 'Edit Pre-Check Report'} goBack={true} />
        <div style={{ paddingTop: 100, textAlign: 'center' }}>
          <text.T14 style={{ color: '#9CA3AF' }}>{tr.loading || 'Memuat...'}</text.T14>
        </div>
      </div>
    );
  }

  const headerTitle = mode === 'create'
    ? (tr.titleCreate || 'Buat Pre-Check Report')
    : (tr.titleEdit || 'Edit Pre-Check Report');

  return (
    <div className='screen' style={{ backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
      {renderAddModal()}
      <components.Header title={headerTitle} goBack={true} />

      <div className='container' style={{ paddingTop: 80, paddingBottom: 120, paddingLeft: 16, paddingRight: 16 }}>

        {/* Type selector */}
        <div style={{ marginBottom: 20 }}>
          <text.H5 style={{ marginBottom: 10 }}>{tr.typeLabel || 'Tipe Laporan'}</text.H5>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['persiapan', 'pemeriksaan'] as const).map(tp => (
              <div
                key={tp}
                onClick={() => setReportType(tp)}
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: 10, textAlign: 'center',
                  border: `2px solid ${reportType === tp ? theme.colors.mainColor : '#E5E7EB'}`,
                  backgroundColor: reportType === tp ? '#EFF6FF' : '#fff',
                  cursor: 'pointer',
                }}
              >
                <text.T14 style={{
                  fontWeight: '700', fontSize: 13,
                  color: reportType === tp ? theme.colors.mainColor : '#6B7280',
                }}>
                  {tp === 'persiapan' ? (tr.typePreparation || 'Persiapan') : (tr.typeInspection || 'Pemeriksaan')}
                </text.T14>
              </div>
            ))}
          </div>
        </div>

        {/* Vessel selector */}
        <div style={{ marginBottom: 20, position: 'relative' }}>
          <text.H5 style={{ marginBottom: 8 }}>{tr.vesselLabel || 'Kapal (opsional)'}</text.H5>
          <div
            onClick={() => setShowVesselDropdown(prev => !prev)}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 10,
              border: `1px solid ${showVesselDropdown ? theme.colors.mainColor : '#E5E7EB'}`,
              fontSize: 15, backgroundColor: '#fff', boxSizing: 'border-box',
              cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}
          >
            <text.T14 style={{ color: vesselId ? theme.colors.mainDark : '#9CA3AF' }}>
              {vesselId
                ? (vessels.find(v => v.id === vesselId)?.name || tr.vesselLabel || 'Kapal')
                : (tr.vesselPlaceholder || 'Pilih kapal...')}
            </text.T14>
            <text.T14 style={{ color: '#9CA3AF', fontSize: 12 }}>{showVesselDropdown ? '▲' : '▼'}</text.T14>
          </div>
          {showVesselDropdown && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
              backgroundColor: '#fff', borderRadius: 10, border: '1px solid #E5E7EB',
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)', maxHeight: 220, overflowY: 'auto',
            }}>
              <div style={{ padding: '8px 12px', borderBottom: '1px solid #F3F4F6' }}>
                <input
                  type="text"
                  placeholder={tr.searchVessel || 'Cari kapal...'}
                  value={vesselSearch}
                  onChange={e => setVesselSearch(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  style={{
                    width: '100%', padding: '6px 10px', borderRadius: 8,
                    border: '1px solid #E5E7EB', fontSize: 14, outline: 'none',
                    fontFamily: 'inherit', boxSizing: 'border-box',
                  }}
                />
              </div>
              {vesselId && (
                <div
                  onClick={() => { setVesselId(null); setVesselSearch(''); setShowVesselDropdown(false); }}
                  style={{ padding: '10px 14px', cursor: 'pointer', color: '#EF4444', fontSize: 14, borderBottom: '1px solid #F3F4F6' }}
                >
                  ✕ {tr.clearVessel || 'Hapus pilihan'}
                </div>
              )}
              {vessels
                .filter(v => v.name.toLowerCase().includes(vesselSearch.toLowerCase()))
                .map(v => (
                  <div
                    key={v.id}
                    onClick={() => { setVesselId(v.id); setVesselSearch(''); setShowVesselDropdown(false); }}
                    style={{
                      padding: '10px 14px', cursor: 'pointer', fontSize: 14,
                      backgroundColor: vesselId === v.id ? '#EFF6FF' : '#fff',
                      color: vesselId === v.id ? theme.colors.mainColor : theme.colors.mainDark,
                      fontWeight: vesselId === v.id ? '600' : 'normal',
                    }}
                  >
                    {v.name}
                  </div>
                ))
              }
            </div>
          )}
        </div>

        {/* Date */}
        <div style={{ marginBottom: 20 }}>
          <text.H5 style={{ marginBottom: 8 }}>{tr.dateLabel || 'Tanggal'}</text.H5>
          <input
            type="date"
            value={reportDate}
            onChange={e => setReportDate(e.target.value)}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 10,
              border: '1px solid #E5E7EB', fontSize: 15, outline: 'none',
              fontFamily: 'inherit', backgroundColor: '#fff', boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Checklist items */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <text.H5>{tr.checklistLabel || 'Item Checklist'}</text.H5>
            <div onClick={() => setShowAddModal(true)} style={{ cursor: 'pointer' }}>
              <text.T14 style={{ color: theme.colors.mainColor, fontWeight: '600' }}>
                + {tr.addItem || 'Tambah'}
              </text.T14>
            </div>
          </div>
          <custom.InputField
            placeholder={tr.searchPlaceholder || 'Cari item checklist...'}
            value={search}
            onChange={(e: any) => setSearch(e.target.value)}
            leftIcon={<SearchIcon size={20} color="#9CA3AF" />}
            containerStyle={{ marginBottom: 10 }}
          />
          {renderChecklist()}
        </div>

        {renderSelectedItems()}

        {/* Note */}
        <div style={{ marginBottom: 24 }}>
          <text.H5 style={{ marginBottom: 8 }}>{tr.noteLabel || 'Catatan'}</text.H5>
          <textarea
            placeholder={tr.notePlaceholder || 'Tambahkan catatan...'}
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={4}
            style={{
              width: '100%', padding: '12px 14px', borderRadius: 10,
              border: '1px solid #E5E7EB', fontSize: 15, outline: 'none',
              fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
              backgroundColor: '#fff',
            }}
          />
        </div>
      </div>

      {/* Fixed bottom buttons */}
      <div style={{
        position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
        width: '100%', maxWidth: 650,
        backgroundColor: '#fff', padding: '12px 16px',
        borderTop: '1px solid #E5E7EB', display: 'flex', gap: 10,
        boxSizing: 'border-box',
      }}>
        <components.Button
          title={saving ? (tr.saving || 'Menyimpan...') : (tr.saveDraft || 'Simpan Draft')}
          style={{ backgroundColor: '#F3F4F6', color: theme.colors.mainDark, padding: '12px' }}
          containerStyle={{ flex: 1 }}
          onClick={() => !saving && handleSave('draft')}
        />
        <components.Button
          title={saving ? (tr.saving || 'Menyimpan...') : (tr.markReady || 'Tandai Siap')}
          style={{ backgroundColor: theme.colors.mainColor, color: '#fff', padding: '12px' }}
          containerStyle={{ flex: 1 }}
          onClick={() => !saving && handleSave('ready')}
        />
      </div>
    </div>
  );
};
